import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import PasswordChangeDialog from './PasswordChangeDialog';
import {
  LayoutDashboard, Users, GraduationCap, BookOpen, Calendar, ClipboardList,
  Megaphone, DollarSign, FileText, Settings, LogOut, Menu, X,
  UserCheck, PenLine, BarChart3, Award, Lock, FileBarChart, Upload,
  MessageSquare, School, Newspaper, Mail
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets-4nw71qhi.emergentagent.net/job_school-mgmt-72/artifacts/eluovc6p_ChatGPT%20Image%20Sep%2015%2C%202026%2C%2009_07_38%20AM.png';

const portalConfigs = {
  admin: {
    label: 'Admin Portal',
    badge: 'bg-indigo-950 text-amber-300 border border-amber-400/40',
    links: [
      { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/admin/students', icon: GraduationCap, label: 'Students' },
      { path: '/admin/teachers', icon: Users, label: 'Teachers' },
      { path: '/admin/parents', icon: UserCheck, label: 'Parents' },
      { path: '/admin/classes', icon: BookOpen, label: 'Classes & Subjects' },
      { path: '/admin/timetable', icon: Calendar, label: 'Timetable' },
      { path: '/admin/announcements', icon: Megaphone, label: 'Announcements' },
      { path: '/admin/news-events', icon: Newspaper, label: 'News & Events' },
      { path: '/admin/finance', icon: DollarSign, label: 'Finance & Invoices' },
      { path: '/admin/admissions', icon: FileText, label: 'Admissions' },
      { path: '/admin/attendance-reports', icon: FileBarChart, label: 'Attendance Reports' },
      { path: '/admin/email-templates', icon: Mail, label: 'Email Templates' },
      { path: '/admin/academic-config', icon: Settings, label: 'Academic Config' },
    ],
  },
  teacher: {
    label: 'Teacher Portal',
    badge: 'bg-emerald-950 text-emerald-300 border border-emerald-400/40',
    links: [
      { path: '/teacher', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/teacher/classes', icon: School, label: 'My Classes' },
      { path: '/teacher/attendance', icon: ClipboardList, label: 'Attendance' },
      { path: '/teacher/assignments', icon: PenLine, label: 'Assignments' },
      { path: '/teacher/gradebook', icon: Award, label: 'Gradebook' },
      { path: '/teacher/timetable', icon: Calendar, label: 'My Timetable' },
      { path: '/teacher/announcements', icon: Megaphone, label: 'Announcements' },
      { path: '/teacher/messages', icon: MessageSquare, label: 'Messages' },
    ],
  },
  student: {
    label: 'Student Portal',
    badge: 'bg-sky-950 text-sky-300 border border-sky-400/40',
    links: [
      { path: '/student', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/student/timetable', icon: Calendar, label: 'My Timetable' },
      { path: '/student/assignments', icon: PenLine, label: 'Assignments' },
      { path: '/student/homework', icon: Upload, label: 'Submit Homework' },
      { path: '/student/results', icon: BarChart3, label: 'My Results' },
      { path: '/student/report-card', icon: FileText, label: 'Report Card' },
      { path: '/student/class-info', icon: School, label: 'My Class' },
      { path: '/student/announcements', icon: Megaphone, label: 'Announcements' },
    ],
  },
  parent: {
    label: 'Parent Portal',
    badge: 'bg-amber-950 text-amber-300 border border-amber-400/40',
    links: [
      { path: '/parent', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/parent/academics', icon: BarChart3, label: 'Academics' },
      { path: '/parent/attendance', icon: ClipboardList, label: 'Attendance' },
      { path: '/parent/timetable', icon: Calendar, label: 'Timetable' },
      { path: '/parent/teachers', icon: GraduationCap, label: 'Teachers' },
      { path: '/parent/finance', icon: DollarSign, label: 'Fees & Invoices' },
      { path: '/parent/messages', icon: MessageSquare, label: 'Messages' },
      { path: '/parent/announcements', icon: Megaphone, label: 'Announcements' },
    ],
  },
};

export default function PortalLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPwChange, setShowPwChange] = useState(false);
  const role = user?.role || 'admin';
  const config = portalConfigs[role] || portalConfigs.admin;

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]" data-testid="portal-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col portal-sidebar" data-testid="portal-sidebar">
        <div className="p-5 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Tadam" className="w-9 h-9 object-contain" />
            <div>
              <p className="font-bold text-white text-sm leading-tight">Tadam Academy</p>
              <span className={`text-[9px] px-2 py-0.5 rounded-full ${config.badge}`}>{config.label}</span>
            </div>
          </Link>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {config.links.map(link => {
            const Icon = link.icon;
            const isActive = location.pathname === link.path || (link.path !== `/${role}` && location.pathname.startsWith(link.path + '/'));
            const isExact = location.pathname === link.path;
            return (
              <Link key={link.path} to={link.path} className={`nav-item ${isExact || isActive ? 'active' : ''}`} data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <Icon className="w-4 h-4 flex-shrink-0" /><span>{link.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-1">
          <button onClick={() => setShowPwChange(true)} className="nav-item w-full text-slate-300 hover:text-white" data-testid="change-password-btn">
            <Lock className="w-4 h-4" /><span>Change Password</span>
          </button>
          <button onClick={handleLogout} className="nav-item w-full text-red-300 hover:text-red-200" data-testid="logout-button">
            <LogOut className="w-4 h-4" /><span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 portal-sidebar w-[260px] animate-slide-left">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="Tadam" className="w-8 h-8 object-contain" />
                <span className="font-bold text-white text-sm">Tadam Academy</span>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="text-white/60"><X className="w-5 h-5" /></button>
            </div>
            <nav className="py-3 overflow-y-auto flex-1">
              {config.links.map(link => {
                const Icon = link.icon;
                return (
                  <Link key={link.path} to={link.path} onClick={() => setSidebarOpen(false)} className={`nav-item ${location.pathname === link.path ? 'active' : ''}`}>
                    <Icon className="w-4 h-4" /><span>{link.label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="p-4 border-t border-white/10 space-y-1">
              <button onClick={() => { setShowPwChange(true); setSidebarOpen(false); }} className="nav-item w-full text-slate-300"><Lock className="w-4 h-4" /><span>Change Password</span></button>
              <button onClick={handleLogout} className="nav-item w-full text-red-300"><LogOut className="w-4 h-4" /><span>Logout</span></button>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-40 bg-white border-b border-slate-200 px-4 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setSidebarOpen(true)} data-testid="mobile-sidebar-toggle"><Menu className="w-5 h-5 text-slate-600" /></button>
            <h2 className="text-sm font-bold text-[#1B2A4A] hidden sm:block">{config.label}</h2>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#1B2A4A] flex items-center justify-center text-white text-xs font-bold">{user?.name?.charAt(0) || 'U'}</div>
              <div className="hidden sm:block">
                <p className="text-sm font-semibold text-slate-800 leading-tight">{user?.name}</p>
                <p className="text-[10px] text-slate-500 capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 overflow-auto">{children}</main>
      </div>

      <PasswordChangeDialog open={showPwChange} onOpenChange={setShowPwChange} />
    </div>
  );
}
